/**
 * India income tax, FY 2026-27 (assessment year 2027-28), individuals. Slabs unchanged from FY 2025-26 (Union Budget 2026).
 * Data checked 2026-10-11 against Budget 2026 coverage; review each February after the Union Budget.
 */
export type Regime = "new" | "old";
export type AgeGroup = "below60" | "60to80" | "above80";

export interface Slab { upTo: number; rate: number }

export const TAX_YEAR_LABEL = "FY 2026-27";

const NEW_SLABS: Slab[] = [
  { upTo: 400000, rate: 0 }, { upTo: 800000, rate: 0.05 }, { upTo: 1200000, rate: 0.1 }, { upTo: 1600000, rate: 0.15 },
  { upTo: 2000000, rate: 0.2 }, { upTo: 2400000, rate: 0.25 }, { upTo: Infinity, rate: 0.3 },
];
const OLD_SLABS: Record<AgeGroup, Slab[]> = {
  below60: [{ upTo: 250000, rate: 0 }, { upTo: 500000, rate: 0.05 }, { upTo: 1000000, rate: 0.2 }, { upTo: Infinity, rate: 0.3 }],
  "60to80": [{ upTo: 300000, rate: 0 }, { upTo: 500000, rate: 0.05 }, { upTo: 1000000, rate: 0.2 }, { upTo: Infinity, rate: 0.3 }],
  above80: [{ upTo: 500000, rate: 0 }, { upTo: 1000000, rate: 0.2 }, { upTo: Infinity, rate: 0.3 }],
};

const NEW = { standardDeduction: 75000, rebateLimit: 1200000, rebateAmount: 60000, npsCapShare: 0.14 };
const OLD = { standardDeduction: 50000, rebateLimit: 500000, rebateAmount: 12500, npsCapShare: 0.1 };

/** Surcharge tiers on income tax (lower bound of TOTAL income, rate). The new regime tops out at 25%. */
const SURCHARGE_NEW = [{ over: 5000000, rate: 0.1 }, { over: 10000000, rate: 0.15 }, { over: 20000000, rate: 0.25 }];
const SURCHARGE_OLD = [...SURCHARGE_NEW, { over: 50000000, rate: 0.37 }];

export const CESS = 0.04;

export function slabTax(taxable: number, slabs: Slab[]): number {
  let tax = 0, last = 0;
  for (const s of slabs) {
    if (taxable <= last) break;
    tax += (Math.min(taxable, s.upTo) - last) * s.rate;
    last = s.upTo;
  }
  return tax;
}

export function slabBreakdown(taxable: number, slabs: Slab[]) {
  const rows: { from: number; to: number; rate: number; amount: number; tax: number }[] = [];
  let last = 0;
  for (const s of slabs) {
    if (taxable <= last) break;
    const amount = Math.min(taxable, s.upTo) - last;
    rows.push({ from: last, to: Math.min(taxable, s.upTo), rate: s.rate, amount, tax: amount * s.rate });
    last = s.upTo;
  }
  return rows;
}

/** Tax plus surcharge, with marginal relief so the extra tax never exceeds the income above the threshold. */
function withSurcharge(income: number, taxFn: (inc: number) => number, tiers: { over: number; rate: number }[]): { surcharge: number; total: number } {
  const tier = [...tiers].reverse().find((t) => income > t.over);
  const tax = taxFn(income);
  if (!tier) return { surcharge: 0, total: tax };
  const gross = tax * (1 + tier.rate);
  const atThreshold = withSurcharge(tier.over, taxFn, tiers).total;
  const capped = Math.min(gross, atThreshold + (income - tier.over));
  return { surcharge: capped - tax, total: capped };
}

export interface IndiaInput {
  regime: Regime;
  age: AgeGroup;
  salary: number;
  otherIncome: number;
  /** Old regime only: 80C, 80D, HRA, home-loan interest etc. */
  deductions: number;
  /** Employer contribution to NPS, 80CCD(2): capped at 14% of salary (new) / 10% (old); salary is treated as basic. */
  employerNps: number;
  hasSalary?: boolean;
}

export interface IndiaResult {
  regime: Regime;
  taxableIncome: number;
  slabTax: number;
  rebate: number;
  marginalRelief: number;
  surcharge: number;
  cess: number;
  total: number;
  effectiveRate: number;
  monthly: number;
  rows: ReturnType<typeof slabBreakdown>;
  standardDeduction: number;
  npsAllowed: number;
}

export function computeIndiaTax(i: IndiaInput): IndiaResult {
  const cfg = i.regime === "new" ? NEW : OLD;
  const slabs = i.regime === "new" ? NEW_SLABS : OLD_SLABS[i.age];
  const salary = Math.max(0, i.salary), other = Math.max(0, i.otherIncome);
  const std = salary > 0 ? Math.min(cfg.standardDeduction, salary) : 0;
  const nps = Math.min(Math.max(0, i.employerNps), salary * cfg.npsCapShare);
  const ded = i.regime === "old" ? Math.max(0, i.deductions) : 0;
  const taxable = Math.max(0, salary + other - std - nps - ded);

  const base = slabTax(taxable, slabs);
  let afterRebate = base, rebate = 0, relief = 0;
  if (taxable <= cfg.rebateLimit) {
    rebate = Math.min(base, cfg.rebateAmount);
    afterRebate = base - rebate;
  } else if (i.regime === "new") {
    // Marginal relief on the 87A rebate: tax can't exceed the income above the rebate limit.
    const cap = taxable - cfg.rebateLimit;
    if (base > cap) { relief = base - cap; afterRebate = cap; }
  }
  const sur = withSurcharge(taxable, (inc) => {
    const b = slabTax(inc, slabs);
    if (inc <= cfg.rebateLimit) return Math.max(0, b - cfg.rebateAmount);
    return i.regime === "new" ? Math.min(b, inc - cfg.rebateLimit) : b;
  }, i.regime === "new" ? SURCHARGE_NEW : SURCHARGE_OLD);
  const beforeCess = taxable > SURCHARGE_NEW[0].over ? sur.total : afterRebate;
  const surcharge = taxable > SURCHARGE_NEW[0].over ? sur.surcharge : 0;
  const cess = beforeCess * CESS;
  const total = beforeCess + cess;
  const income = salary + other;
  return {
    regime: i.regime, taxableIncome: taxable, slabTax: base, rebate, marginalRelief: relief, surcharge, cess, total,
    effectiveRate: income > 0 ? (total / income) * 100 : 0, monthly: total / 12, rows: slabBreakdown(taxable, slabs), standardDeduction: std, npsAllowed: nps,
  };
}
